import { useCallback, useEffect, useRef, useState } from "react";
import { formatQuestion } from "../game/questions";
import { CORRECT_POINTS, ROUND_DURATION_MS, WRONG_POINTS, type Question, type RoundResult } from "../game/types";

interface GameProps {
  name: string;
  questions: Question[];
  onFinish: (result: RoundResult) => void;
}

export function Game({ name, questions, onFinish }: GameProps) {
  const [index, setIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [picked, setPicked] = useState<number | null>(null);

  const scoreRef = useRef(0);
  const correctCountRef = useRef(0);
  const wrongCountRef = useRef(0);
  const indexRef = useRef(0);
  const pickedRef = useRef<number | null>(null);
  const doneRef = useRef(false);
  const startedAtRef = useRef(Date.now());

  const timerTextRef = useRef<HTMLSpanElement>(null);
  const timerBarRef = useRef<HTMLDivElement>(null);

  const finish = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    onFinish({
      name,
      score: scoreRef.current,
      correctCount: correctCountRef.current,
      wrongCount: wrongCountRef.current,
      timeUsedMs: Math.min(ROUND_DURATION_MS, Date.now() - startedAtRef.current),
    });
  }, [name, onFinish]);

  // 60 FPS Direct-DOM Timer (0 React Re-renders while time ticks)
  useEffect(() => {
    startedAtRef.current = Date.now();
    let animId: number;

    const tick = () => {
      const elapsed = Date.now() - startedAtRef.current;
      const leftMs = Math.max(0, ROUND_DURATION_MS - elapsed);
      const secondsStr = (leftMs / 1000).toFixed(2);
      const ratio = leftMs / ROUND_DURATION_MS;

      if (timerTextRef.current) {
        timerTextRef.current.innerText = `${secondsStr}s`;
        if (leftMs < 10_000) {
          timerTextRef.current.classList.add("danger");
        } else {
          timerTextRef.current.classList.remove("danger");
        }
      }

      if (timerBarRef.current) {
        timerBarRef.current.style.width = `${ratio * 100}%`;
        if (leftMs < 10_000) {
          timerBarRef.current.classList.add("low");
        } else {
          timerBarRef.current.classList.remove("low");
        }
      }

      if (leftMs <= 0) {
        finish();
      } else {
        animId = requestAnimationFrame(tick);
      }
    };

    animId = requestAnimationFrame(tick);
    return () => {
      if (animId) cancelAnimationFrame(animId);
    };
  }, [finish]);

  const answer = (value: number) => {
    if (pickedRef.current !== null || doneRef.current) return;
    const currentQ = questions[indexRef.current];
    if (!currentQ) return;

    pickedRef.current = value;
    setPicked(value);

    const ok = value === currentQ.answer;
    if (ok) {
      scoreRef.current += CORRECT_POINTS;
      correctCountRef.current += 1;
      setScore(scoreRef.current);
    } else {
      scoreRef.current += WRONG_POINTS;
      wrongCountRef.current += 1;
      setScore(scoreRef.current);
    }

    window.setTimeout(() => {
      if (doneRef.current) return;
      pickedRef.current = null;
      setPicked(null);

      const nextIndex = indexRef.current + 1;
      if (nextIndex < questions.length) {
        indexRef.current = nextIndex;
        setIndex(nextIndex);
      } else {
        finish();
      }
    }, 400);
  };

  const handleAnswerEvent = (e: React.SyntheticEvent, value: number) => {
    e.preventDefault();
    (e.currentTarget as HTMLElement)?.blur();
    (document.activeElement as HTMLElement)?.blur();
    answer(value);
  };

  const question = questions[index];
  const finishedSet = !question;
  const answered = picked !== null;
  const opClass = question ? `op-${question.op}` : "";
  const opLabel = question ? (question.op === "add" ? "Addition (+)" : question.op === "sub" ? "Subtraction (−)" : question.op === "mul" ? "Multiplication (×)" : "Division (÷)") : "";

  return (
    <div className="game">
      <header className="game-header">
        <span className="player-name">{name}</span>
        <div className="stat-box">
          <span className="stat-label">Score</span>
          <span className="stat-value">{score}</span>
        </div>
        <div className="stat-box">
          <span className="stat-label">Time</span>
          <span ref={timerTextRef} className="stat-value">60.00s</span>
        </div>
      </header>

      <div className="timer-bar">
        <div ref={timerBarRef} className="timer-bar-fill" style={{ width: "100%" }} />
      </div>

      <main className={`question-card ${opClass} ${answered ? (question && picked === question.answer ? "correct" : "wrong") : ""}`}>
        {finishedSet ? (
          <div className="set-done">
            <div className="set-done-score">{score}</div>
            <div className="set-done-label">Set complete!</div>
          </div>
        ) : (
          <>
            <span className={`op-badge op-badge-${question.op}`}>{opLabel}</span>

            <div className="question-display">
              <span className="question">{formatQuestion(question)}</span>
              <span className="equals">=</span>
              <span className="question-mark">?</span>
            </div>

            <div className="options-grid">
              {question.options.map((opt, i) => {
                let cls = "option-btn";
                if (answered) {
                  if (opt === question.answer) cls += " correct";
                  else if (opt === picked) cls += " wrong";
                  else cls += " dim";
                }
                return (
                  <button
                    key={i}
                    type="button"
                    className={cls}
                    disabled={answered}
                    onPointerDown={e => handleAnswerEvent(e, opt)}
                    onClick={e => handleAnswerEvent(e, opt)}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>

            <div className={`feedback ${answered ? (picked === question.answer ? "correct" : "wrong") : ""}`}>
              {answered &&
                (picked === question.answer ? (
                  <span className="feedback-text">Correct!</span>
                ) : (
                  <span className="feedback-text">
                    Oops! {formatQuestion(question)} = {question.answer}
                  </span>
                ))}
            </div>
          </>
        )}
      </main>

      <p className="game-hint">Tap an answer to lock in your choice</p>
    </div>
  );
}
